import {
  CellType,
  type DroneEntity,
  type DroneType,
  type FloatingText,
  type GridSlicerConfig,
  type GridSlicerInput,
  type Particle,
  type Player,
  type Point,
  type PowerUpEntity,
  type PowerUpType,
  type QixEntity,
  type ShockwaveRing,
  type SparkEntity,
} from './types';

export { CellType };
export type {
  DroneEntity,
  DroneType,
  FloatingText,
  GridSlicerConfig,
  GridSlicerInput,
  Particle,
  Player,
  Point,
  PowerUpEntity,
  PowerUpType,
  QixEntity,
  ShockwaveRing,
  SparkEntity,
};

export class GridSlicerEngine {
  public readonly cols: number;
  public readonly rows: number;
  public readonly width: number;
  public readonly height: number;
  public readonly cellWidth: number;
  public readonly cellHeight: number;

  public grid: CellType[][] = [];
  public player!: Player;
  public qix!: QixEntity;
  public sparks: SparkEntity[] = [];
  public drones: DroneEntity[] = [];
  public powerUps: PowerUpEntity[] = [];
  public shockwaves: ShockwaveRing[] = [];
  public particles: Particle[] = [];
  public floatingTexts: FloatingText[] = [];

  public wave: number = 1;
  public score: number = 0;
  public comboMultiplier: number = 1;
  public maxCombo: number = 1;
  public claimedPercentage: number = 0;
  public targetPercentage: number = 75;
  public isGameOver: boolean = false;
  public isWaveCleared: boolean = false;
  public stasisTimer: number = 0;
  public message: string = '';
  public messageTimer: number = 0;

  private moveAccumulator: number = 0;
  private totalPlayableCells: number = 0;
  private nextId: number = 1;

  constructor(config: GridSlicerConfig = {}) {
    this.cols = config.cols ?? 80;
    this.rows = config.rows ?? 60;
    this.width = config.width ?? 800;
    this.height = config.height ?? 600;
    this.cellWidth = this.width / this.cols;
    this.cellHeight = this.height / this.rows;
    this.targetPercentage = config.targetPercentage ?? 75;
    this.totalPlayableCells = (this.cols - 2) * (this.rows - 2);

    this.initGame(config.initialLives ?? 3);
  }

  public initGame(initialLives: number = 3): void {
    this.wave = 1;
    this.score = 0;
    this.comboMultiplier = 1;
    this.maxCombo = 1;
    this.isGameOver = false;
    this.isWaveCleared = false;
    this.stasisTimer = 0;
    this.player = {
      x: Math.floor(this.cols / 2),
      y: this.rows - 1,
      lives: initialLives,
      score: 0,
      isDrawing: false,
      fastMode: false,
      trail: [],
      fuseTimer: 0,
      invulnTimer: 0,
      shieldActive: false,
      turboTimer: 0,
      pulseCooldown: 0,
    };

    this.initWave();
  }

  public initWave(): void {
    this.isWaveCleared = false;
    this.claimedPercentage = 0;
    this.stasisTimer = 0;
    this.powerUps = [];
    this.shockwaves = [];
    this.particles = [];
    this.floatingTexts = [];

    // Initialize grid: outer edge is BORDER, interior is EMPTY
    this.grid = [];
    for (let r = 0; r < this.rows; r++) {
      const row: CellType[] = [];
      for (let c = 0; c < this.cols; c++) {
        if (r === 0 || r === this.rows - 1 || c === 0 || c === this.cols - 1) {
          row.push(CellType.BORDER);
        } else {
          row.push(CellType.EMPTY);
        }
      }
      this.grid.push(row);
    }

    // Reset player to bottom border center
    this.player.x = Math.floor(this.cols / 2);
    this.player.y = this.rows - 1;
    this.player.isDrawing = false;
    this.player.trail = [];
    this.player.fuseTimer = 0;
    this.player.invulnTimer = 1.5;

    // Initialize Qix boss in the center
    const baseSpeed = 120 + (this.wave - 1) * 25;
    const angle = Math.PI / 4;
    this.qix = {
      x: this.width / 2,
      y: this.height / 2,
      vx: Math.cos(angle) * baseSpeed,
      vy: Math.sin(angle) * baseSpeed,
      radius: 26,
      points: [],
      stunTimer: 0,
    };
    this.updateQixPoints();

    // Initialize Border Sparks
    this.sparks = [];
    const numSparks = Math.min(5, 1 + Math.floor(this.wave / 2));
    for (let i = 0; i < numSparks; i++) {
      this.sparks.push({
        x: Math.floor((this.cols / (numSparks + 1)) * (i + 1)),
        y: 0,
        dirX: i % 2 === 0 ? 1 : -1,
        dirY: 0,
        speed: Math.max(0.03, 0.08 - this.wave * 0.005),
        moveTimer: 0,
      });
    }

    // Initialize Open-Grid Sub-Drones (trappable inside slices!)
    this.drones = [];
    const numDrones = Math.min(6, 1 + this.wave);
    const types: DroneType[] = ['scout', 'hunter', 'pulsar'];
    for (let i = 0; i < numDrones; i++) {
      const type = types[i % types.length];
      const dAngle = ((i + 0.5) / numDrones) * Math.PI * 2;
      const dSpeed = (type === 'hunter' ? 110 : 85) + this.wave * 10;
      const distFromCenter = 80 + (i % 2) * 50;
      this.drones.push({
        id: `drone_${this.nextId++}`,
        type,
        x: this.width / 2 + Math.cos(dAngle) * distFromCenter,
        y: this.height / 2 + Math.sin(dAngle) * distFromCenter,
        vx: Math.cos(dAngle + 1.1) * dSpeed,
        vy: Math.sin(dAngle + 1.1) * dSpeed,
        radius: type === 'pulsar' ? 10 : 8,
        angle: dAngle,
        pulsePhase: i * 1.5,
        stunTimer: 0,
      });
    }

    this.message = `WAVE ${this.wave} - CLAIM ${this.targetPercentage}%`;
    this.messageTimer = 2.0;
  }

  public update(dt: number, input: GridSlicerInput, audioEnergy: number = 0.5): void {
    if (this.isGameOver) {
      if (input.restart) {
        this.initGame(3);
      }
      return;
    }

    if (this.isWaveCleared) {
      this.messageTimer -= dt;
      this.updateEffects(dt);
      if (this.messageTimer <= 0 || input.restart) {
        this.wave += 1;
        this.initWave();
      }
      return;
    }

    if (this.messageTimer > 0) {
      this.messageTimer = Math.max(0, this.messageTimer - dt);
    }

    if (this.player.invulnTimer > 0) {
      this.player.invulnTimer = Math.max(0, this.player.invulnTimer - dt);
    }

    if (this.player.turboTimer > 0) {
      this.player.turboTimer = Math.max(0, this.player.turboTimer - dt);
    }

    if (this.player.pulseCooldown > 0) {
      this.player.pulseCooldown = Math.max(0, this.player.pulseCooldown - dt);
    }

    if (this.stasisTimer > 0) {
      this.stasisTimer = Math.max(0, this.stasisTimer - dt);
    }

    this.player.fastMode = Boolean(input.fastCut ?? input.fire);

    // Sonic Repulsion Pulse ability (triggered explicitly via pulsePressed, or pressing fire while safe on border)
    if (input.pulsePressed || (!this.player.isDrawing && input.restart && this.player.pulseCooldown <= 0)) {
      this.triggerSonicPulse(audioEnergy);
    }

    // 1. Update Player Movement on Grid
    this.updatePlayer(dt, input, audioEnergy);

    // 2. Update Qix Entity
    this.updateQix(dt, audioEnergy);

    // 3. Update Open-Grid Sub-Drones
    this.updateDrones(dt, audioEnergy);

    // 4. Update Border Sparks
    this.updateSparks(dt);

    // 5. Update Power-Ups & Visual Effects
    this.updatePowerUps(dt);
    this.updateEffects(dt);
  }

  public triggerSonicPulse(audioEnergy: number = 0.5): boolean {
    if (this.player.pulseCooldown > 0 || this.isGameOver) return false;

    this.player.pulseCooldown = 1.2;
    const px = this.player.x * this.cellWidth + this.cellWidth / 2;
    const py = this.player.y * this.cellHeight + this.cellHeight / 2;
    const pulseRadius = 130 + audioEnergy * 40;

    this.addShockwave(px, py, pulseRadius, '#ffee00', 0.45);
    this.addParticles(px, py, '#ffee00', 14);

    // Repulse Qix if within range
    const qdx = this.qix.x - px;
    const qdy = this.qix.y - py;
    const qDist = Math.hypot(qdx, qdy);
    if (qDist > 0 && qDist <= pulseRadius) {
      const speed = Math.hypot(this.qix.vx, this.qix.vy) || 150;
      this.qix.vx = (qdx / qDist) * speed;
      this.qix.vy = (qdy / qDist) * speed;
      this.qix.stunTimer = Math.max(this.qix.stunTimer, 0.8);
    }

    // Repulse & Stun nearby Drones
    for (const drone of this.drones) {
      const ddx = drone.x - px;
      const ddy = drone.y - py;
      const dDist = Math.hypot(ddx, ddy);
      if (dDist > 0 && dDist <= pulseRadius) {
        const dSpeed = Math.hypot(drone.vx, drone.vy) || 100;
        drone.vx = (ddx / dDist) * dSpeed;
        drone.vy = (ddy / dDist) * dSpeed;
        drone.stunTimer = Math.max(drone.stunTimer, 1.5);
      }
    }

    // Destroy nearby Sparks within pulse radius
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      const sx = s.x * this.cellWidth + this.cellWidth / 2;
      const sy = s.y * this.cellHeight + this.cellHeight / 2;
      if (Math.hypot(sx - px, sy - py) <= pulseRadius * 0.75) {
        this.sparks.splice(i, 1);
        this.score += 100;
        this.player.score = this.score;
        this.addParticles(sx, sy, '#00ffff', 12);
        this.addFloatingText('+100 ZAP!', sx, sy, '#ffee00');
      }
    }

    return true;
  }

  private updatePlayer(dt: number, input: GridSlicerInput, audioEnergy: number): void {
    let baseInterval = this.player.fastMode ? 0.03 : 0.055;
    if (this.player.turboTimer > 0) {
      baseInterval *= 0.6;
    }

    this.moveAccumulator += dt;

    let dx = 0;
    let dy = 0;
    if (input.left) dx = -1;
    else if (input.right) dx = 1;
    else if (input.up) dy = -1;
    else if (input.down) dy = 1;

    if (dx === 0 && dy === 0) {
      // Stationary while drawing increases fuse burn timer!
      if (this.player.isDrawing) {
        this.player.fuseTimer += dt;
        if (this.player.fuseTimer > 2.5) {
          this.onPlayerHit('FUSE DETONATED!');
        }
      }
      return;
    }

    // Player is moving; reset fuse timer
    this.player.fuseTimer = 0;

    if (this.moveAccumulator < baseInterval) return;
    this.moveAccumulator = 0;

    const nextX = Math.max(0, Math.min(this.cols - 1, this.player.x + dx));
    const nextY = Math.max(0, Math.min(this.rows - 1, this.player.y + dy));

    if (nextX === this.player.x && nextY === this.player.y) return;

    const targetCell = this.grid[nextY][nextX];

    if (!this.player.isDrawing) {
      if (targetCell === CellType.BORDER || targetCell === CellType.CLAIMED) {
        this.player.x = nextX;
        this.player.y = nextY;
      } else if (targetCell === CellType.EMPTY) {
        // Start drawing a new slice
        this.player.isDrawing = true;
        this.player.trail = [
          { x: this.player.x, y: this.player.y },
          { x: nextX, y: nextY },
        ];
        this.grid[nextY][nextX] = CellType.TRAIL;
        this.player.x = nextX;
        this.player.y = nextY;
      }
    } else {
      // Currently drawing a trail
      if (targetCell === CellType.EMPTY) {
        this.player.trail.push({ x: nextX, y: nextY });
        this.grid[nextY][nextX] = CellType.TRAIL;
        this.player.x = nextX;
        this.player.y = nextY;
      } else if (targetCell === CellType.BORDER || targetCell === CellType.CLAIMED) {
        // Completed the slice!
        this.player.x = nextX;
        this.player.y = nextY;
        this.completeSlice(audioEnergy);
      } else if (targetCell === CellType.TRAIL) {
        // Self-intersection with own trail!
        const prev = this.player.trail[this.player.trail.length - 2];
        if (prev && prev.x === nextX && prev.y === nextY) {
          return;
        }
        this.onPlayerHit('SELF-INTERSECTION!');
      }
    }
  }

  private completeSlice(audioEnergy: number): void {
    const qixCol = Math.max(1, Math.min(this.cols - 2, Math.floor(this.qix.x / this.cellWidth)));
    const qixRow = Math.max(1, Math.min(this.rows - 2, Math.floor(this.qix.y / this.cellHeight)));

    // Flood fill from Qix position to find all EMPTY cells connected to Qix
    const visited: boolean[][] = Array.from({ length: this.rows }, () =>
      Array(this.cols).fill(false)
    );

    const queue: Point[] = [{ x: qixCol, y: qixRow }];
    visited[qixRow][qixCol] = true;

    let head = 0;
    while (head < queue.length) {
      const curr = queue[head++];
      const neighbors = [
        { x: curr.x + 1, y: curr.y },
        { x: curr.x - 1, y: curr.y },
        { x: curr.x, y: curr.y + 1 },
        { x: curr.x, y: curr.y - 1 },
      ];

      for (const n of neighbors) {
        if (
          n.x >= 0 &&
          n.x < this.cols &&
          n.y >= 0 &&
          n.y < this.rows &&
          !visited[n.y][n.x] &&
          this.grid[n.y][n.x] === CellType.EMPTY
        ) {
          visited[n.y][n.x] = true;
          queue.push(n);
        }
      }
    }

    // Spawn shockwave rings along the completed trail
    for (let i = 0; i < this.player.trail.length; i += 4) {
      const tp = this.player.trail[i];
      this.addShockwave(
        tp.x * this.cellWidth + this.cellWidth / 2,
        tp.y * this.cellHeight + this.cellHeight / 2,
        36,
        this.player.fastMode ? '#ff00aa' : '#00ffcc',
        0.35
      );
    }

    // Convert all unvisited EMPTY cells and TRAIL cells to CLAIMED
    let newlyClaimed = 0;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.grid[r][c] === CellType.TRAIL) {
          this.grid[r][c] = CellType.CLAIMED;
          newlyClaimed++;
        } else if (this.grid[r][c] === CellType.EMPTY && !visited[r][c]) {
          this.grid[r][c] = CellType.CLAIMED;
          newlyClaimed++;
        }
      }
    }

    // Update BORDER designations on edges between CLAIMED/EMPTY
    this.updateGridBorders();

    const prevPercentage = this.claimedPercentage;
    // Calculate Claimed Percentage
    let totalClaimed = 0;
    for (let r = 1; r < this.rows - 1; r++) {
      for (let c = 1; c < this.cols - 1; c++) {
        if (this.grid[r][c] === CellType.CLAIMED || this.grid[r][c] === CellType.BORDER) {
          totalClaimed++;
        }
      }
    }
    this.claimedPercentage = Math.min(100, Math.floor((totalClaimed / this.totalPlayableCells) * 100));
    const deltaPct = Math.max(0, this.claimedPercentage - prevPercentage);

    // Check if any Sub-Drones were trapped inside newly CLAIMED territory!
    let trappedCount = 0;
    for (let i = this.drones.length - 1; i >= 0; i--) {
      const d = this.drones[i];
      const dCol = Math.max(0, Math.min(this.cols - 1, Math.floor(d.x / this.cellWidth)));
      const dRow = Math.max(0, Math.min(this.rows - 1, Math.floor(d.y / this.cellHeight)));
      if (this.grid[dRow][dCol] !== CellType.EMPTY) {
        trappedCount++;
        const trapBonus = 250 * Math.pow(2, trappedCount - 1) * this.wave;
        this.score += trapBonus;
        this.addShockwave(d.x, d.y, 70, '#ff00aa', 0.5);
        this.addParticles(d.x, d.y, '#ff00aa', 18);
        this.addFloatingText(`TRAPPED +${trapBonus}!`, d.x, d.y, '#ff00aa');
        this.drones.splice(i, 1);
      }
    }

    // Calculate Slice Points + Progressive Combo + Large Cut Bonus
    const basePtsPerCell = this.player.fastMode ? 10 : 25; // slow cut gives 2.5x bonus
    const harmonicMultiplier = 1 + Math.floor(audioEnergy * 2);
    let sliceScore = newlyClaimed * basePtsPerCell * harmonicMultiplier * this.comboMultiplier * this.wave;

    // Large Cut Bonus (>= 8% in a single slice) spawns Power-Up & stuns nearby hazards!
    const px = this.player.x * this.cellWidth + this.cellWidth / 2;
    const py = this.player.y * this.cellHeight + this.cellHeight / 2;
    if (deltaPct >= 8) {
      const largeCutBonus = Math.floor(500 * Math.pow(1.3, Math.min(15, deltaPct - 7)));
      sliceScore += largeCutBonus;
      const midPt = this.player.trail[Math.floor(this.player.trail.length / 2)] ?? {
        x: Math.max(1, this.player.x - 4),
        y: Math.max(1, this.player.y - 4),
      };
      this.spawnPowerUp(
        midPt.x * this.cellWidth + this.cellWidth / 2,
        midPt.y * this.cellHeight + this.cellHeight / 2
      );
      this.addShockwave(px, py, 160, '#00ffcc', 0.55);
      this.addFloatingText(`MEGA CUT +${deltaPct}%!`, px, py - 15, '#00ffcc');

      // Harmonic stun on nearby drones
      for (const d of this.drones) {
        if (Math.hypot(d.x - px, d.y - py) <= 160) {
          d.stunTimer = Math.max(d.stunTimer, 2.5);
        }
      }
    }

    this.score += sliceScore;
    this.player.score = this.score;
    this.comboMultiplier += 1;
    this.maxCombo = Math.max(this.maxCombo, this.comboMultiplier);

    this.addFloatingText(
      `+${sliceScore} (${this.comboMultiplier - 1}x)`,
      px,
      py,
      this.player.fastMode ? '#ff00aa' : '#ffbb00'
    );

    // Reset player drawing state
    this.player.isDrawing = false;
    this.player.trail = [];
    this.player.fuseTimer = 0;

    // Reposition Sparks if they are trapped on interior CLAIMED cells
    for (const spark of this.sparks) {
      if (this.grid[spark.y][spark.x] === CellType.CLAIMED) {
        const borderCell = this.findNearestBorderCell(spark.x, spark.y);
        spark.x = borderCell.x;
        spark.y = borderCell.y;
      }
    }

    // Check Wave Target Completion
    if (this.claimedPercentage >= this.targetPercentage) {
      this.isWaveCleared = true;
      const clearBonus = 1000 * this.wave;
      this.score += clearBonus;
      this.player.score = this.score;
      this.message = `STAGE CLEARED! ${this.claimedPercentage}% CLAIMED`;
      this.messageTimer = 2.0;
    } else {
      const trapText = trappedCount > 0 ? ` [${trappedCount} TRAPPED!]` : '';
      this.message = `SLICE +${sliceScore} PTS (${this.claimedPercentage}%)${trapText}`;
      this.messageTimer = 1.2;
    }
  }

  public spawnPowerUp(x: number, y: number, forcedType?: PowerUpType): PowerUpEntity {
    const types: PowerUpType[] = ['stasis', 'turbo', 'shield'];
    const type = forcedType ?? types[this.nextId % types.length];
    const pu: PowerUpEntity = {
      id: `pu_${this.nextId++}`,
      type,
      x,
      y,
      radius: 11,
      timer: 10.0,
    };
    this.powerUps.push(pu);
    return pu;
  }

  private updatePowerUps(dt: number): void {
    const px = this.player.x * this.cellWidth + this.cellWidth / 2;
    const py = this.player.y * this.cellHeight + this.cellHeight / 2;

    for (let i = this.powerUps.length - 1; i >= 0; i--) {
      const pu = this.powerUps[i];
      pu.timer -= dt;
      if (pu.timer <= 0) {
        this.powerUps.splice(i, 1);
        continue;
      }

      if (Math.hypot(pu.x - px, pu.y - py) <= pu.radius + 14) {
        this.applyPowerUp(pu.type);
        this.addParticles(pu.x, pu.y, '#00ff88', 15);
        this.powerUps.splice(i, 1);
      }
    }
  }

  public applyPowerUp(type: PowerUpType): void {
    const px = this.player.x * this.cellWidth + this.cellWidth / 2;
    const py = this.player.y * this.cellHeight + this.cellHeight / 2;
    this.score += 250;
    this.player.score = this.score;

    if (type === 'stasis') {
      this.stasisTimer = 4.5;
      this.addFloatingText('STASIS FIELD ACTIVE!', px, py - 12, '#00ffff');
      this.message = 'STASIS FIELD: HAZARDS FROZEN!';
    } else if (type === 'turbo') {
      this.player.turboTimer = 7.0;
      this.addFloatingText('TURBO SLICER!', px, py - 12, '#ffbb00');
      this.message = 'TURBO SLICER SPEED ENGAGED!';
    } else if (type === 'shield') {
      this.player.shieldActive = true;
      this.addFloatingText('ENERGY SHIELD ONLINE!', px, py - 12, '#00ff66');
      this.message = 'ENERGY SHIELD ACTIVE (1 HIT PROTECTION)';
    }
    this.messageTimer = 1.5;
  }

  private updateGridBorders(): void {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.grid[r][c] === CellType.CLAIMED || this.grid[r][c] === CellType.BORDER) {
          const hasEmptyNeighbor =
            (r > 0 && this.grid[r - 1][c] === CellType.EMPTY) ||
            (r < this.rows - 1 && this.grid[r + 1][c] === CellType.EMPTY) ||
            (c > 0 && this.grid[r][c - 1] === CellType.EMPTY) ||
            (c < this.cols - 1 && this.grid[r][c + 1] === CellType.EMPTY);

          const isOuterEdge = r === 0 || r === this.rows - 1 || c === 0 || c === this.cols - 1;
          this.grid[r][c] = hasEmptyNeighbor || isOuterEdge ? CellType.BORDER : CellType.CLAIMED;
        }
      }
    }
  }

  private findNearestBorderCell(x: number, y: number): Point {
    let minDist = Infinity;
    let nearest: Point = { x: 0, y: 0 };

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.grid[r][c] === CellType.BORDER) {
          const dist = Math.hypot(c - x, r - y);
          if (dist < minDist) {
            minDist = dist;
            nearest = { x: c, y: r };
          }
        }
      }
    }
    return nearest;
  }

  private updateQix(dt: number, audioEnergy: number): void {
    if (this.qix.stunTimer > 0) {
      this.qix.stunTimer = Math.max(0, this.qix.stunTimer - dt);
      this.updateQixPoints();
      return;
    }

    const stasisFactor = this.stasisTimer > 0 ? 0.15 : 1.0;
    const pulseFactor = (1 + audioEnergy * 0.3) * stasisFactor;
    this.qix.x += this.qix.vx * dt * pulseFactor;
    this.qix.y += this.qix.vy * dt * pulseFactor;

    const qCol = Math.max(0, Math.min(this.cols - 1, Math.floor(this.qix.x / this.cellWidth)));
    const qRow = Math.max(0, Math.min(this.rows - 1, Math.floor(this.qix.y / this.cellHeight)));

    const nextCol = Math.max(
      0,
      Math.min(this.cols - 1, Math.floor((this.qix.x + Math.sign(this.qix.vx) * this.qix.radius) / this.cellWidth))
    );
    const nextRow = Math.max(
      0,
      Math.min(this.rows - 1, Math.floor((this.qix.y + Math.sign(this.qix.vy) * this.qix.radius) / this.cellHeight))
    );

    if (this.grid[qRow][nextCol] !== CellType.EMPTY) {
      this.qix.vx *= -1;
      this.qix.x += this.qix.vx * dt;
    }
    if (this.grid[nextRow][qCol] !== CellType.EMPTY) {
      this.qix.vy *= -1;
      this.qix.y += this.qix.vy * dt;
    }

    this.updateQixPoints();

    if (this.player.isDrawing && this.player.invulnTimer <= 0) {
      for (const p of this.player.trail) {
        const cellX = p.x * this.cellWidth + this.cellWidth / 2;
        const cellY = p.y * this.cellHeight + this.cellHeight / 2;
        const dist = Math.hypot(this.qix.x - cellX, this.qix.y - cellY);
        if (dist < this.qix.radius + 8) {
          this.onPlayerHit('QIX INTERCEPTED SLICE LINE!');
          break;
        }
      }
    }
  }

  private updateQixPoints(): void {
    const numPts = 6;
    this.qix.points = [];
    const time = Date.now() * 0.003;

    for (let i = 0; i < numPts; i++) {
      const angle = (i / numPts) * Math.PI * 2 + time;
      const r = this.qix.radius * (0.8 + Math.sin(time * 2 + i) * 0.3);
      this.qix.points.push({
        x: this.qix.x + Math.cos(angle) * r,
        y: this.qix.y + Math.sin(angle) * r,
      });
    }
  }

  private updateDrones(dt: number, audioEnergy: number): void {
    const stasisFactor = this.stasisTimer > 0 ? 0.1 : 1.0;
    const speedFactor = (1 + audioEnergy * 0.25) * stasisFactor;

    for (const d of this.drones) {
      d.pulsePhase += dt * 4;
      if (d.stunTimer > 0) {
        d.stunTimer = Math.max(0, d.stunTimer - dt);
        continue;
      }

      d.x += d.vx * dt * speedFactor;
      d.y += d.vy * dt * speedFactor;
      d.angle = d.type === 'hunter' ? Math.atan2(d.vy, d.vx) : d.angle + dt * 2.5;

      const dCol = Math.max(0, Math.min(this.cols - 1, Math.floor(d.x / this.cellWidth)));
      const dRow = Math.max(0, Math.min(this.rows - 1, Math.floor(d.y / this.cellHeight)));
      const nextCol = Math.max(
        0,
        Math.min(this.cols - 1, Math.floor((d.x + Math.sign(d.vx) * d.radius) / this.cellWidth))
      );
      const nextRow = Math.max(
        0,
        Math.min(this.rows - 1, Math.floor((d.y + Math.sign(d.vy) * d.radius) / this.cellHeight))
      );

      if (this.grid[dRow][nextCol] !== CellType.EMPTY) {
        d.vx *= -1;
        d.x += d.vx * dt;
      }
      if (this.grid[nextRow][dCol] !== CellType.EMPTY) {
        d.vy *= -1;
        d.y += d.vy * dt;
      }

      if (this.player.isDrawing && this.player.invulnTimer <= 0) {
        for (const p of this.player.trail) {
          const cellX = p.x * this.cellWidth + this.cellWidth / 2;
          const cellY = p.y * this.cellHeight + this.cellHeight / 2;
          if (Math.hypot(d.x - cellX, d.y - cellY) < d.radius + 6) {
            this.onPlayerHit('DRONE SEVERED SLICE LINE!');
            break;
          }
        }
      }
    }
  }

  private updateSparks(dt: number): void {
    if (this.stasisTimer > 0) return;

    for (const spark of this.sparks) {
      spark.moveTimer += dt;
      if (spark.moveTimer < spark.speed) continue;
      spark.moveTimer = 0;

      const nextX = spark.x + spark.dirX;
      const nextY = spark.y + spark.dirY;

      if (
        nextX >= 0 &&
        nextX < this.cols &&
        nextY >= 0 &&
        nextY < this.rows &&
        this.grid[nextY][nextX] === CellType.BORDER
      ) {
        spark.x = nextX;
        spark.y = nextY;
      } else {
        const dirs = [
          { x: 0, y: -1 },
          { x: 1, y: 0 },
          { x: 0, y: 1 },
          { x: -1, y: 0 },
        ];
        let found = false;
        for (const d of dirs) {
          if (d.x === -spark.dirX && d.y === -spark.dirY) continue;
          const testX = spark.x + d.x;
          const testY = spark.y + d.y;
          if (
            testX >= 0 &&
            testX < this.cols &&
            testY >= 0 &&
            testY < this.rows &&
            this.grid[testY][testX] === CellType.BORDER
          ) {
            spark.dirX = d.x;
            spark.dirY = d.y;
            spark.x = testX;
            spark.y = testY;
            found = true;
            break;
          }
        }
        if (!found) {
          spark.dirX *= -1;
          spark.dirY *= -1;
        }
      }

      if (spark.x === this.player.x && spark.y === this.player.y && this.player.invulnTimer <= 0) {
        this.onPlayerHit('SPARK IMPACT!');
      }
    }
  }

  private onPlayerHit(reason: string): void {
    if (this.player.invulnTimer > 0) return;

    const px = this.player.x * this.cellWidth + this.cellWidth / 2;
    const py = this.player.y * this.cellHeight + this.cellHeight / 2;

    // Energy Shield absorbs 1 fatal hit!
    if (this.player.shieldActive) {
      this.player.shieldActive = false;
      this.player.invulnTimer = 1.5;
      this.addShockwave(px, py, 90, '#00ff66', 0.4);
      this.addParticles(px, py, '#00ff66', 16);
      this.addFloatingText('SHIELD ABSORBED HIT!', px, py - 10, '#00ff66');
      this.message = 'SHIELD ABSORBED IMPACT!';
      this.messageTimer = 1.5;
      return;
    }

    this.player.lives -= 1;
    this.player.invulnTimer = 2.0;
    this.comboMultiplier = 1;
    this.message = reason;
    this.messageTimer = 2.0;

    // Spawn 20-particle explosion burst on death
    this.addParticles(px, py, '#ff0055', 20);
    this.addFloatingText(reason, px, py, '#ff0055');

    // Clear active trail cells from grid
    for (const p of this.player.trail) {
      if (this.grid[p.y][p.x] === CellType.TRAIL) {
        this.grid[p.y][p.x] = CellType.EMPTY;
      }
    }

    // Reset player to nearest border
    const nearestBorder = this.findNearestBorderCell(this.player.x, this.player.y);
    this.player.x = nearestBorder.x;
    this.player.y = nearestBorder.y;
    this.player.isDrawing = false;
    this.player.trail = [];
    this.player.fuseTimer = 0;

    if (this.player.lives <= 0) {
      this.isGameOver = true;
      this.message = 'SYSTEM OVERRIDE - GAME OVER';
    }
  }

  private addShockwave(x: number, y: number, maxRadius: number, color: string, maxAge: number): void {
    this.shockwaves.push({
      x,
      y,
      radius: 4,
      maxRadius,
      color,
      alpha: 1,
      age: 0,
      maxAge,
    });
  }

  private addParticles(x: number, y: number, color: string, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 40 + Math.random() * 140;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        size: 2 + Math.random() * 2.5,
        alpha: 1,
        life: 0,
        maxLife: 0.35 + Math.random() * 0.35,
      });
    }
  }

  private addFloatingText(text: string, x: number, y: number, color: string): void {
    this.floatingTexts.push({
      text,
      x,
      y,
      color,
      alpha: 1,
      life: 0,
      maxLife: 0.9,
    });
  }

  private updateEffects(dt: number): void {
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const sw = this.shockwaves[i];
      sw.age += dt;
      sw.radius = 4 + (sw.maxRadius - 4) * Math.min(1, sw.age / sw.maxAge);
      sw.alpha = Math.max(0, 1 - sw.age / sw.maxAge);
      if (sw.age >= sw.maxAge) {
        this.shockwaves.splice(i, 1);
      }
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.alpha = Math.max(0, 1 - p.life / p.maxLife);
      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
      }
    }

    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.life += dt;
      ft.y -= 28 * dt;
      ft.alpha = Math.max(0, 1 - ft.life / ft.maxLife);
      if (ft.life >= ft.maxLife) {
        this.floatingTexts.splice(i, 1);
      }
    }
  }
}
